import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * CinematicLoader - Animation d'ouverture VEA STUDIO
 *
 * Séquence :
 * 1. LOADING - logo doré Véa STUDIO + décompte cinématographique (cercle qui
 *              tourne facon amorce de film, anneau qui se remplit = curseur,
 *              chiffres 4·3·2·1). En fond, la vidéo se met en cache.
 * 2. VIDEO   - la vidéo se joue plein écran (son activable), finit sur le logo.
 * 3. FINALE  - fondu pailleté (étoiles) ; le logo se pose EXACTEMENT sur le
 *              logo central du site (même taille/position) puis s'efface.
 *
 * Son : l'autoplay sonore est bloqué par les navigateurs. La vidéo démarre
 * en silencieux puis le son s'active au 1er clic/touche, ou via le bouton 🔊.
 *
 * Respecte prefers-reduced-motion (passe directement au site).
 */

interface CinematicLoaderProps {
  onComplete: () => void;
  /** Démarre l'intro son activé (déclenchée via le bouton = geste utilisateur). */
  startWithSound?: boolean;
}

const INTRO_VIDEO = "/intro-vea.mp4";
const INTRO_POSTER = "/intro-vea-poster.jpg";
const LOGO_OR = "/vea-logo-or.png";
const GOLD = "#D4AF37";

const COUNT_START = 4;
/**
 * Duree d'affichage d'un chiffre du decompte, en millisecondes.
 *
 * Le decompte 4-3-2-1 precede la video et n'a aucun contenu a preserver,
 * contrairement a elle : c'est donc ici que le temps se gagne le plus
 * simplement. 950 ms par chiffre faisaient 3,8 s d'attente avant meme le debut
 * de la video.
 *
 * ⚠ Les animations du chiffre et du balayage sont calees SUR cette valeur (voir
 * plus bas) : elles etaient ecrites en dur a 0,95 s. Changer le tempo sans
 * elles laissait le chiffre inacheve au moment ou le suivant le remplace.
 */
const TICK = 600; // ms par chiffre

/**
 * Duree maximale de la phase video, en millisecondes.
 *
 * Le fichier dure 8 s et etait joue en entier : avec le decompte (3,8 s), la
 * finale (2 s) et la sortie (0,7 s), un visiteur attendait pres de 15 s avant de
 * voir le site.
 *
 * Valeur choisie d'apres le profil sonore REEL du fichier, mesure demi-seconde
 * par demi-seconde :
 *   0 -> 6,0 s : son plein (-15 a -24 dB), le passage le plus fort vers 3,5 s
 *   6,0 s      : debut du fondu propre au montage (-24,7 dB)
 *   7,5 s      : quasi silence (-39 dB)
 * Couper a 5 s tronquait donc du son a plein niveau. A 6,6 s, on ne perd que la
 * queue d'un fondu deja descendu d'une dizaine de decibels, et le fondu ajoute
 * ci-dessous prolonge celui du montage au lieu de le contredire.
 */
const VIDEO_MAX = 6600;
const FADE_MS = 600;
const TOTAL = COUNT_START * TICK; // durée du décompte / remplissage de l'anneau
const RING_R = 46;
const RING_C = 2 * Math.PI * RING_R;

type Phase = "loading" | "video" | "finale" | "exit";

export default function CinematicLoader({ onComplete, startWithSound = false }: CinematicLoaderProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [videoFade, setVideoFade] = useState(false);
  const [count, setCount] = useState(COUNT_START);
  const [ringFill, setRingFill] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [soundOn, setSoundOn] = useState(startWithSound);
  const [heroBox, setHeroBox] = useState<{ cx: number; cy: number; w: number } | null>(null);
  const [merge, setMerge] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const completedRef = useRef(false);
  const soundRef = useRef(startWithSound);
  // Vrai dès que la vidéo a RÉELLEMENT commencé à jouer (événement onPlaying).
  // Sert au garde-fou anti-écran-noir : si elle ne démarre pas, on passe au site.
  const videoStartedRef = useRef(false);

  // Étoiles / paillettes du final (angles/rayons normalisés, figés)
  const sparkles = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        angle: (i / 24) * Math.PI * 2 + (i % 4),
        rf: 0.55 + ((i * 17) % 60) / 100, // facteur de rayon
        size: 7 + ((i * 13) % 16),
        delay: ((i * 80) % 850) / 1000,
        dur: 0.9 + ((i * 7) % 9) / 10,
      })),
    []
  );

  const finishNow = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setPhase("exit");
    setTimeout(onComplete, 700);
  };

  // Active le son (suite à un geste utilisateur)
  const enableSound = () => {
    soundRef.current = true;
    setSoundOn(true);
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.play().catch(() => {});
    }
  };

  // Montage : retire le splash, précharge la vidéo, écoute le 1er geste (son)
  useEffect(() => {
    const splash = document.getElementById("vea-splash");
    if (splash && splash.parentNode) splash.parentNode.removeChild(splash);

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      finishNow();
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Précharge réel de la vidéo (réchauffe le cache HTTP)
    let cancelled = false;
    (async () => {
      try {
        await fetch(INTRO_VIDEO, { cache: "force-cache" });
      } catch {
        /* repli : lecture en streaming */
      } finally {
        if (!cancelled) setVideoReady(true);
      }
    })();

    // 1er geste utilisateur -> active le son
    const onGesture = () => {
      enableSound();
      remove();
    };
    const remove = () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
    };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    window.addEventListener("touchstart", onGesture);

    // L'anneau commence son remplissage (synchronisé au décompte)
    const raf = requestAnimationFrame(() => setRingFill(100));

    // Garde-fou maître : quoi qu'il arrive (vidéo qui ne démarre pas, phase
    // bloquée, navigateur capricieux), le site apparaît au plus tard après ~13 s.
    // Jamais d'écran figé.
    const master = setTimeout(finishNow, 13000);

    return () => {
      cancelled = true;
      remove();
      cancelAnimationFrame(raf);
      clearTimeout(master);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Décompte 4·3·2·1 (un cran par TICK)
  useEffect(() => {
    if (phase !== "loading" || count <= 0) return;
    const t = setTimeout(() => setCount((c) => c - 1), TICK);
    return () => clearTimeout(t);
  }, [phase, count]);

  // Lancement de la vidéo : décompte fini ET vidéo prête (sécurité 4 s)
  useEffect(() => {
    if (phase !== "loading" || count > 0) return;
    if (videoReady) {
      setPhase("video");
      return;
    }
    const safety = setTimeout(() => setPhase("video"), 4000);
    return () => clearTimeout(safety);
  }, [phase, count, videoReady]);

  // Lecture de la vidéo : le son (la voix) s'active dès la fin de l'intro,
  // au démarrage de la vidéo - repli muet si le navigateur bloque l'autoplay sonore.
  useEffect(() => {
    if (phase !== "video") return;
    const v = videoRef.current;
    if (v) {
      v.loop = false; // jamais en boucle
      v.muted = false; // active la voix à la fin de l'intro
      soundRef.current = true;
      setSoundOn(true);
      v.play().catch(() => {
        // autoplay avec son refusé par le navigateur -> repli muet
        v.muted = true;
        soundRef.current = false;
        setSoundOn(false);
        // Si même l'autoplay MUET est refusé (Firefox en mode strict « bloquer
        // les vidéos »), la vidéo resterait figée sur un poster sombre : on
        // révèle le site tout de suite plutôt que de laisser un écran noir.
        v.play().catch(() => finishNow());
      });
    }
    // Fondu sonore progressif puis passage a la finale. Baisser le volume d'un
    // coup s'entend comme une coupure ; une rampe s'entend comme une fin.
    const debutFondu = Math.max(0, VIDEO_MAX - FADE_MS);
    const tFondu = setTimeout(() => {
      setVideoFade(true);
      const vid = videoRef.current;
      if (vid && !vid.muted) {
        const pas = 50;
        const etapes = FADE_MS / pas;
        let i = 0;
        const rampe = setInterval(() => {
          i += 1;
          vid.volume = Math.max(0, 1 - i / etapes);
          if (i >= etapes) clearInterval(rampe);
        }, pas);
      }
    }, debutFondu);

    const fin = setTimeout(() => setPhase("finale"), VIDEO_MAX);

    // Anti-écran-noir : si la vidéo n'a pas RÉELLEMENT démarré au bout de 3 s
    // (autoplay bloqué, décodage qui cale, poster figé), on révèle le site
    // directement plutôt que de laisser un écran sombre.
    const demarrage = setTimeout(() => {
      if (!videoStartedRef.current) finishNow();
    }, 3000);

    return () => {
      clearTimeout(tFondu);
      clearTimeout(fin);
      clearTimeout(demarrage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Final : mesure le logo hero (avant peinture) puis lance la fusion
  useLayoutEffect(() => {
    if (phase !== "finale") return;
    const el = document.getElementById("vea-hero-logo");
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0) setHeroBox({ cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: r.width });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Déclenche l'animation de fusion (grand centré -> logo hero) puis révèle
  useEffect(() => {
    if (phase !== "finale") return;
    const raf = requestAnimationFrame(() => setMerge(true));
    const t = setTimeout(finishNow, 2000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const isLoading = phase === "loading";
  const isVideo = phase === "video";
  const isFinale = phase === "finale";

  // Géométrie du logo de final (sur le logo hero si trouvé, sinon centré)
  const winW = typeof window !== "undefined" ? window.innerWidth : 1280;
  const winH = typeof window !== "undefined" ? window.innerHeight : 800;
  const fcx = heroBox ? heroBox.cx : winW / 2;
  const fcy = heroBox ? heroBox.cy : winH * 0.4;
  const fw = heroBox ? heroBox.w : Math.min(winW * 0.46, 440);
  // Décalage pour partir du centre de l'écran (taille « vidéo ») et fusionner vers le hero
  const dx = winW / 2 - fcx;
  const dy = winH / 2 - fcy;

  const toggleSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    const v = videoRef.current;
    const next = !soundOn;
    soundRef.current = next;
    setSoundOn(next);
    if (v) {
      v.muted = !next;
      if (next) v.play().catch(() => {});
    }
  };

  return (
    <div
      onClick={isVideo ? () => setPhase("finale") : undefined}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: isFinale || phase === "exit" ? "rgba(13,13,13,0)" : "#0D0D0D",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        opacity: phase === "exit" ? 0 : 1,
        transition: "opacity 0.7s cubic-bezier(0.16,1,0.3,1), background 1.6s cubic-bezier(0.16,1,0.3,1)",
        pointerEvents: phase === "exit" || isFinale ? "none" : "all",
        cursor: isVideo ? "pointer" : "default",
      }}
    >
      {/* Grain cinéma */}
      {!isFinale && phase !== "exit" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E")`,
            opacity: 0.05,
            pointerEvents: "none",
            zIndex: 5,
          }}
        />
      )}

      {/* Vidéo */}
      {(isVideo || isFinale) && (
        <video
          ref={videoRef}
          src={INTRO_VIDEO}
          poster={INTRO_POSTER}
          autoPlay
          muted
          playsInline
          preload="auto"
          onPlaying={() => { videoStartedRef.current = true; }}
          onError={() => finishNow()}
          onEnded={() => setPhase("finale")}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: isVideo && !videoFade ? 1 : 0,
            transition: `opacity ${videoFade ? FADE_MS : 900}ms cubic-bezier(0.16,1,0.3,1)`,
          }}
        />
      )}

      {/* Letterbox pendant la vidéo */}
      {isVideo && (
        <>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "7vh", background: "#000", zIndex: 4, pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "7vh", background: "#000", zIndex: 4, pointerEvents: "none" }} />
        </>
      )}

      {/* PHASE 1 - logo + décompte cinématographique */}
      {isLoading && (
        <div style={{ position: "relative", zIndex: 6, width: "100%", height: "100%" }}>
          {/* Logo centré */}
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={LOGO_OR} alt="VEA STUDIO" style={{ width: "min(46vw, 440px)", height: "auto", display: "block" }} />
          </div>

          {/* Cercle de décompte (amorce ciné + anneau-curseur) sous le logo */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "calc(50% + 200px)",
              transform: "translate(-50%, -50%)",
              width: "132px",
              height: "132px",
            }}
          >
            <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              {/* cercles + croix de visée */}
              <circle cx="50" cy="50" r={RING_R} fill="none" stroke="rgba(212,175,55,0.18)" strokeWidth="0.8" />
              <circle cx="50" cy="50" r="33" fill="none" stroke="rgba(212,175,55,0.12)" strokeWidth="0.4" />
              <line x1="50" y1="3" x2="50" y2="97" stroke="rgba(212,175,55,0.14)" strokeWidth="0.4" />
              <line x1="3" y1="50" x2="97" y2="50" stroke="rgba(212,175,55,0.14)" strokeWidth="0.4" />
              {/* anneau-curseur qui se remplit */}
              <circle
                cx="50"
                cy="50"
                r={RING_R}
                fill="none"
                stroke={GOLD}
                strokeWidth="1.6"
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
                style={{
                  strokeDasharray: RING_C,
                  strokeDashoffset: RING_C * (1 - ringFill / 100),
                  transition: `stroke-dashoffset ${TOTAL}ms linear`,
                  filter: "drop-shadow(0 0 3px rgba(212,175,55,0.7))",
                }}
              />
              {/* amorce balayante (film leader) */}
              <g style={{ transformOrigin: "50px 50px", animation: `veaSweep ${TICK}ms linear infinite` }}>
                <path d="M50 50 L50 4 A46 46 0 0 1 82 22 Z" fill="rgba(212,175,55,0.10)" />
                <line x1="50" y1="50" x2="50" y2="4" stroke={GOLD} strokeWidth="0.7" />
              </g>
            </svg>
            {/* chiffre central */}
            <div
              key={count}
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "'Playfair Display', serif",
                fontSize: "46px",
                fontWeight: 600,
                color: GOLD,
                textShadow: "0 0 16px rgba(212,175,55,0.5)",
                animation: `veaTick ${TICK}ms cubic-bezier(0.16,1,0.3,1)`,
              }}
            >
              {count > 0 ? count : 1}
            </div>
          </div>
        </div>
      )}

      {/* PHASE 3 - le logo fusionne avec le logo hero du site */}
      {isFinale && (
        <div style={{ position: "fixed", inset: 0, zIndex: 6, pointerEvents: "none" }}>
          {/* Groupe (logo + halo + paillettes) : part grand & centré (taille vidéo)
              puis se pose EXACTEMENT sur le logo hero pendant que le noir s'efface */}
          <div
            style={{
              position: "fixed",
              left: fcx,
              top: fcy,
              width: 0,
              height: 0,
              transformOrigin: "center",
              transform: merge ? "translate(0px, 0px) scale(1)" : `translate(${dx}px, ${dy}px) scale(1.5)`,
              transition: "transform 1.6s cubic-bezier(0.16, 1, 0.3, 1)",
              willChange: "transform",
            }}
          >
            {/* Halo doré */}
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "60vmin",
                height: "60vmin",
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(212,175,55,0.3) 0%, rgba(212,175,55,0.07) 35%, transparent 70%)",
                animation: "veaHalo 2s ease-out forwards",
              }}
            />
            {/* Logo : reste opaque et se cale pile sur le logo hero (fusion) */}
            <img
              src={LOGO_OR}
              alt="VEA STUDIO"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: fw,
                height: "auto",
                transform: "translate(-50%, -50%)",
                filter: merge ? "drop-shadow(0 0 0 rgba(212,175,55,0))" : "drop-shadow(0 0 35px rgba(212,175,55,0.6))",
                transition: "filter 1.6s ease",
              }}
            />
            {/* Étoiles / paillettes autour du logo */}
            {sparkles.map((s, i) => {
              const radius = (fw / 2) * s.rf;
              const x = Math.cos(s.angle) * radius;
              const y = Math.sin(s.angle) * radius * 0.7;
              return (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: x,
                    top: y,
                    width: s.size,
                    height: s.size,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    style={{ width: "100%", height: "100%", opacity: 0, animation: `veaSparkle ${s.dur}s ease-in-out ${s.delay}s forwards` }}
                  >
                    <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill={GOLD} />
                  </svg>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bouton son (pendant loading + vidéo) */}
      {(isLoading || isVideo) && (
        <button
          onClick={toggleSound}
          aria-label={soundOn ? "Couper le son" : "Activer le son"}
          style={{
            position: "absolute",
            bottom: "1.5rem",
            left: "1.5rem",
            zIndex: 7,
            background: soundOn ? "rgba(212,175,55,0.18)" : "rgba(0,0,0,0.35)",
            border: "1px solid rgba(212,175,55,0.45)",
            color: "rgba(212,175,55,0.9)",
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "0.6rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            padding: "0.5rem 0.9rem",
            borderRadius: "999px",
            cursor: "pointer",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
        >
          {soundOn ? "🔊 Son" : "🔇 Activer le son"}
        </button>
      )}

      {/* Bouton Passer */}
      {!isFinale && phase !== "exit" && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            finishNow();
          }}
          style={{
            position: "absolute",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 7,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "rgba(212,175,55,0.85)",
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "0.6rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            padding: "0.5rem 1rem",
            borderRadius: "999px",
            cursor: "pointer",
            backdropFilter: "blur(4px)",
          }}
        >
          Passer
        </button>
      )}

      <style>{`
        @keyframes veaSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes veaTick { 0% { opacity: 0; transform: scale(1.5); } 35% { opacity: 1; } 100% { opacity: 0.9; transform: scale(1); } }
        @keyframes veaSparkle { 0% { opacity: 0; transform: scale(0) rotate(0deg); } 45% { opacity: 1; transform: scale(1) rotate(35deg); } 100% { opacity: 0; transform: scale(0.2) rotate(70deg); } }
        @keyframes veaHalo { 0% { opacity: 0; transform: translate(-50%,-50%) scale(0.4); } 40% { opacity: 1; } 100% { opacity: 0; transform: translate(-50%,-50%) scale(1.3); } }
      `}</style>
    </div>
  );
}
